import { FC, useCallback } from 'react';
import styled from 'styled-components';
import { Post } from '@vs-online-only-app/state';
import { darken, IconButton, Stack } from '@mui/material';
import { PlayArrowRounded } from '@mui/icons-material';

const Container = styled.div<{ $isPreview?: boolean }>`
  width: 100%;
  height: ${({ $isPreview }) => ($isPreview ? '200px' : '40vh')};
`;

const Video = styled.video`
  height: 100%;
  width: 100%;
  max-height: 40vh;
  background-color: black;
  object-fit: contain;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
`;

const PlayButton = styled(IconButton)`
  background: ${({ theme }) => theme.palette.common.white};
  color: ${({ theme }) => theme.palette.primary.main};
  box-shadow: ${({ theme }) => theme.shadows[1]};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  &:hover {
    background-color: ${({ theme }) => darken(theme.palette.common.white, 0.1)};
  }
`;

const categorizedMediaItems = (mediaItems: Post['media']) => {
  const image = mediaItems.find(
    ({ type }) => type.startsWith('image/') && !type.includes('thumbnail'),
  );
  const thumbnail = mediaItems.find(({ type }) => type.includes('thumbnail'));
  const video = mediaItems.find(({ type }) => type.startsWith('video/'));
  return { image, thumbnail, video };
};

type Props = { media: Post['media']; isPreview?: boolean };

const PostMedia: FC<Props> = ({ media, isPreview }) => {
  const { image, thumbnail, video } = categorizedMediaItems(media);

  const videoCallbackRef = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      if (!videoElement) return;
      videoElement.volume = 0.5;
    },
    [],
  );

  if (!image && !video) return null;

  return (
    <Container $isPreview={isPreview}>
      {video && !isPreview && (
        <Video
          src={video.url}
          controls
          aria-label={video.name}
          poster={thumbnail?.url}
          ref={videoCallbackRef}
        />
      )}
      {video && isPreview && thumbnail && (
        <Stack position="relative" height="100%">
          <PlayButton as="span">
            <PlayArrowRounded />
          </PlayButton>
          <Image src={thumbnail.url} />
        </Stack>
      )}
      {image && <Image src={image.url} alt={image.name} />}
    </Container>
  );
};

export default PostMedia;
